<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end mb-3">
        <span>
          <span v-if="completing">
            <b-button size="sm" variant="primary" disabled>
              <b-spinner small />
              {{ 'saving_message' | i18n }}
            </b-button>
          </span>
          <span v-else>
            <b-button style="margin-right: 10px" size="sm" variant="primary" @click="() => completeProject({ silent: true })">
              {{ 'Save' | i18n }}
            </b-button>
            <b-button size="sm" variant="primary" v-b-modal.saveAndSubmitModal>{{ 'save_submit' | i18n }}</b-button>
            <SubmitModal id="saveAndSubmitModal" :completeProject="completeProject" :isOnSiteSupportOnly="project.isOnSiteSupportOnly" />
          </span>
        </span>
      </div>
      <HistoryModal :project="project" />
      <div class="d-flex justify-content-between mb-3">
        <div class="d-flex flex-column w-50">
          <Checkbox
            v-model="translateProject"
            placeholder="Translate project"
            :left="0"
            :bottom="5"
            form-group-id="translate_project"
            :input="onChangeTranslateProject"
          />
          <Select
            v-if="translateProject"
            v-model="language"
            :options="languagesOptions"
            :on-change="() => generateTemplate(false)"
            form-group-id="Language"
            placeholder="Language"
            input-placeholder="Select language"
            class="mb-2"
          />
        </div>
        <div>
          <b-button v-if="history.length" size="sm" variant="outline-primary" style="margin-top: 3px" @click="showHistory">
            {{ 'History' | i18n }}
          </b-button>
        </div>
      </div>
      <div class="overflow-x-hidden overflow-auto pr-1 flex1">
        <Group
          v-for="group in selectedTemplate.groups"
          :key="group.id"
          :group="group"
          :set-field-value="setFieldValue"
          :fields-value="fieldsValue"
          :get-file="getFile"
          :getFileURL="getFileURL"
          :show-validation="true"
          :feeds="feeds"
          :translate-language="language"
          :enable-translation="translateProject"
          group-name="Topic"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';

import api from '../../../../api';
import { NotificationMixin, AuthMixin, ReportsMixin } from '../../../../../mixins';
import { TRANSLATE_LANGUAGE, ENTRY_TYPES, VALIDATION_RULES, ENTRY_TYPES_DRAW } from '../../../../../../../../constants';
import Checkbox from '../../../common/checkbox';
import Select from '../../../common/select';
import Group from '../../../reports/cases/components/group';
import HistoryModal from '../project-chat/components/history-modal';
import SubmitModal from '../project-chat/components/submit-modal';

export default {
  title: 'Project Details',
  components: { Select, Checkbox, Group, HistoryModal, SubmitModal },
  mixins: [NotificationMixin, AuthMixin, ReportsMixin],
  data() {
    return {
      completing: false,
      id: null,
      languages: [],
      project: {},
      selectedTemplate: {},
      fieldsTextInputValue: {},
      feeds: {},
      excludeFromPDFValues: {},
      fieldsValue: {},
      translateProject: false,
      language: '',
      languagesOptions: [],
      history: [],
    };
  },
  async created() {
    try {
      this.id = this.$route.params.milestoneId;

      if (this.id) {
        const project = await api.projectMilestones.getById(this.id);
        this.project = project.data;

        const history = await api.projectMilestones.getHistoryById(this.project.id);
        this.history = history.data;

        this.languages = Object.keys(TRANSLATE_LANGUAGE).map((key) => ({ name: key, value: TRANSLATE_LANGUAGE[key] }));
        this.languagesOptions = this.languages.map((l) => ({ value: l.value, text: l.name }));
      }

      await this.setDefaultLanguage();
      await this.generateTemplate(true);
    } catch (err) {
      this.showNotification({ type: 'error', message: err.message });
    }
  },
  methods: {
    mapMilestonePayload(params) {
      const payload = new FormData();

      params.fields.forEach((field) => {
        payload.append(`field_${field.key}`, this.reportsService.mapFieldValue(field));
        if (field.showTextInput) {
          payload.append(`field_${field.key}_textInputValue`, typeof field.textInputValue !== 'undefined' ? field.textInputValue : null);
        }
      });

      return payload;
    },
    async completeProject({
      silent,
      translateLanguage,
      firstChatTranslateLanguage,
      secondChatTranslateLanguage,
      isTranslatedChatContent,
      isReportWithTranslation,
      complete,
    }) {
      try {
        this.completing = true;

        const fields = this.mapMilestonePayload({
          fields: this.project.template.fields.map((currentField) => ({
            ...currentField,
            value: this.fieldsValue[currentField.id] || this.fieldsValue[currentField.key],
          })),
        });

        await api.projectMilestones.updateFields(fields, this.project.id);

        await api.projectMilestones.submit({}, this.project.id, {
          silent,
          translateLanguage,
          firstChatTranslateLanguage,
          secondChatTranslateLanguage,
          complete,
          isTranslatedChatContent,
          isReportWithTranslation,
        });

        const history = await api.projectMilestones.getHistoryById(this.project.id);
        this.history = history.data;
        this.showNotification({ type: 'success', message: this.$translate('submitted_successfully') });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      } finally {
        this.completing = false;
      }
    },
    showHistory() {
      this.$root.$emit('bv::show::modal', 'history-modal');
    },
    async onChangeTranslateProject() {
      await this.setDefaultLanguage();
      await this.generateTemplate(false);
    },
    async setDefaultLanguage() {
      this.language = null;

      try {
        const auth = localStorage.getItem('auth');
        const lng = localStorage.getItem('lng');

        if (lng) {
          const str = JSON.parse(lng);
          this.language = str.toUpperCase();
        }

        if (auth) {
          const json = JSON.parse(auth);
          if (json.language) {
            this.language = json.language.toUpperCase();
          }
        }
      } catch (e) {
        this.language = null;
      }
    },
    async generateTemplate(initial) {
      const template = this.project.template;

      const groups = [];
      const groupsFields = {};

      const translation = template.translation;

      for (const currentField of template.fields) {
        if (currentField.showTextInput) {
          Vue.set(this.fieldsTextInputValue, currentField.key, currentField.textInputValue);
          Vue.set(this.fieldsTextInputValue, currentField.id, currentField.textInputValue);
        }

        if (currentField.entryType === ENTRY_TYPES.FEED) {
          this.$set(this.feeds, this.project.fields[currentField.key], []);
        }

        if (!groupsFields[currentField.groupId]) {
          groupsFields[currentField.groupId] = [];
        }
        currentField.required = currentField.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);

        const isTranslatedTitle = translation[currentField.key] && translation[currentField.key][this.language];

        const temporaryTitle = this.translateProject && !isTranslatedTitle ? await this.getTranslation(currentField.title) : currentField.title;
        const title = isTranslatedTitle ? translation[currentField.key][this.language] : temporaryTitle;

        const description =
          isTranslatedTitle || this.translateProject ? await this.getTranslation(currentField.description) : currentField.description;

        const mappedValues = [];
        if (currentField.mappedValues.length && this.translateProject) {
          for (const mappedValue of currentField.mappedValues) {
            const translatedValue = await this.getTranslation(mappedValue.value);
            const newMappedValue = translatedValue && translatedValue.data ? { ...mappedValue, value: translatedValue.data } : mappedValue;
            mappedValues.push(newMappedValue);
          }
        }

        groupsFields[currentField.groupId].push({
          ...currentField,
          mappedValues: mappedValues.length ? mappedValues : currentField.mappedValues,
          title: title.data || title,
          description: (description ? description.data : '') || description,
        });
      }

      for (const currentGroup of template.groups) {
        const isTranslatedName = translation[currentGroup.id] && translation[currentGroup.id][this.language];
        const temporaryName = this.translateProject && !isTranslatedName ? await this.getTranslation(currentGroup.name) : currentGroup.name;
        const name = isTranslatedName ? translation[currentGroup.id][this.language] : temporaryName;

        const data = {
          name: name.data || name,
          id: currentGroup.id,
          isConfirmation: currentGroup.isConfirmation,
          isFile: currentGroup.isFile,
          file: currentGroup.file,
          fields: groupsFields[currentGroup.id],
        };
        groups.push(data);
      }

      if (initial) {
        this.fieldsValue = {
          ...this.project.fields,
          ...this.project.report.fields,
        };
      }

      this.selectedTemplate = {
        name: template.name,
        id: template.id,
        groups,
      };
    },
    async getTranslation(message) {
      try {
        if (!message) {
          return '';
        }
        return await api.translate.translate({
          message,
          language: this.language,
        });
      } catch (e) {
        console.log(e, 'Translation error');
      }
    },
    async getFile(id) {
      try {
        await axios(this.getFileURL(id));
        window.open(this.getFileURL(id), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    getFileURL(id) {
      const auth = this.authStorageService.getAuth();
      const token = auth.access_token_link;
      const url = axios.defaults.baseURL;
      return `${url}/api/v1/one/project-milestone/${this.project.id}/file/${id}?authorization=${token}`;
    },
    async setFieldValue(field, value) {
      const isDrawField = ENTRY_TYPES_DRAW.includes(field.entryType);
      if (this.fieldLoaded) {
        this.disablePrevent = false;
      }
      if (isDrawField) {
        this.fieldsValue[field.id] = value;
        if (!value) {
          this.fieldsValue[field.key] = value;
        }
      } else {
        this.fieldsValue[field.key] = value;
      }
    },
  },
};
</script>

<style lang="scss">
.overflow-x-hidden {
  overflow-x: hidden !important;
}
</style>
